/**
 * Tournament types for Arena and Swiss tournaments
 */

import type { TimeControlPreset, TimeControlKey } from './time-control.js';
import { resolveTimeControl } from './time-control.js';
import type { GameVariant } from './enums.js';
import type { OpeningMode } from './game-mode.js';

/**
 * Tournament status
 */
export type TournamentStatus = 'scheduled' | 'active' | 'finished' | 'cancelled';

/**
 * Participant status in tournament
 */
export type ParticipantStatus = 'active' | 'paused' | 'withdrawn';

/**
 * Tournament format
 */
export type TournamentFormat = 'arena' | 'swiss' | 'round_robin';

/**
 * Arena scoring constants
 */
export const ARENA_SCORING = {
  WIN: 2,
  DRAW: 1,
  LOSS: 0,
} as const;

/**
 * Quanto vale una vittoria nei tornei a turni, e quindi anche un riposo.
 *
 * Costante intermedia e non due letterali, perche' l'uguaglianza fra la vittoria
 * e il bye e' un'INVARIANTE e non una coincidenza: `puntiPerIlRiposo` accredita
 * al riposo svizzero `SWISS_SCORING.BYE * manche` proprio perche' un turno
 * riposato vale un turno vinto. Finche' erano due `1` scritti a mano, quel
 * legame viveva in un commento — e un commento non diventa rosso.
 *
 * Il GIRONE non usa `BYE`: li' il riposo vale zero, e il perche' sta su
 * `puntiPerIlRiposo`, in fondo a questo file.
 */
const VITTORIA_A_TURNI = 2;

/**
 * Punteggio dei tornei a turni: **2 / 1 / 0**, la scala della Federazione
 * Italiana Dama.
 *
 * Non e' piu' la scala FIDE (1 / 0,5 / 0) che questo file dichiarava: la patta
 * vale un punto pieno, e ogni totale di classifica e' quindi un INTERO. Da cui
 * tre cose che il codice a valle da' per vere:
 *  - l'accoppiatore raggruppa per punteggio esatto (`groupByScore`) e la chiave
 *    non e' piu' un `float`;
 *  - la classifica non ha piu' bisogno di scrivere mezzi punti — `puntiTorneo`
 *    lato web resta come rete per i dati storici, non per il caso normale;
 *  - la riga delle regole interpola questi numeri, quindi non stampa piu'
 *    «+0.5» col punto decimale nelle sei lingue che usano la virgola.
 *
 * Le ARENE erano gia' su questa scala (`ARENA_SCORING`, qui sopra) e non sono
 * state toccate. Il Sonneborn-Berger conserva un `* 0.5` che NON e' la scala ma
 * la sua definizione: vedi `calculateSonnebornBerger`.
 */
export const SWISS_SCORING = {
  WIN: VITTORIA_A_TURNI,
  DRAW: 1,
  LOSS: 0,
  /** «Come una vittoria», e legato a `WIN` per costruzione. */
  BYE: VITTORIA_A_TURNI,
} as const;

/**
 * Gli esiti che lo STORICO di un partecipante puo' contenere, in ordine
 * cronologico, uno per partita (migration 122, `results_history`).
 *
 * Una sorgente sola per tre posti che devono dire la stessa cosa: il CHECK
 * della colonna, il tipo del client e la forma che la pedina prende a schermo.
 * `bye` c'e' perche' il riposo non e' una partita giocata ma conta come tale
 * (`games_played`, e nello svizzero anche due punti): senza una voce sua
 * sarebbe indistinguibile da una vittoria, che e' esattamente la bugia che
 * questa colonna esiste per non raccontare.
 *
 * Non c'e' `winPlus`: quello e' dell'arena, dove serie e boost fanno valere una
 * vittoria piu' di due punti. Nei tornei a turni non esistono ne' l'una ne'
 * l'altro.
 */
export const ESITI_STORICO = ['win', 'draw', 'loss', 'bye'] as const;

export type EsitoStorico = (typeof ESITI_STORICO)[number];

/**
 * Swiss round info (emitted via socket events)
 */
export interface SwissRoundInfo {
  roundNumber: number;
  totalRounds: number;
  pairings: SwissPairing[];
}

/**
 * Swiss pairing for a round
 */
export interface SwissPairing {
  whiteId: string;
  whiteUsername: string;
  whiteRating: number;
  blackId: string;
  blackUsername: string;
  blackRating: number;
  gameId?: string;
  /** 'forfeit' = doppio no-show, 0 punti a entrambi (vedi migration 086). */
  result?: 'white' | 'black' | 'draw' | 'forfeit' | null;
  board: number;
}

/**
 * Swiss bye info
 */
export interface SwissBye {
  userId: string;
  username: string;
  roundNumber: number;
}

/**
 * Swiss standings entry
 */
export interface SwissStanding {
  rank: number;
  userId: string;
  username: string;
  rating: number;
  points: number;
  buchholz: number;
  sonnebornBerger: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesDrawn: number;
  gamesLost: number;
  performance: number;
}

/**
 * Una fascia di calendario per gli Swiss: la forma in cui una riga di
 * `schedule_slots` con `kind = 'swiss'` arriva allo scheduler.
 *
 * Gemello di `ArenaTemplate` (arena.ts), con in piu' i turni e senza il tetto
 * di tabella: lo Swiss pesca l'apertura con un altro meccanismo — una per
 * turno, con la lettera scritta a mano in `swiss-handler.ts` — e finche' quel
 * meccanismo non viene unificato, `ballotCeiling` qui non avrebbe niente da
 * comandare.
 */
export interface SwissTournamentTemplate {
  name: string;
  /*
   * `TimeControlKey` e non `TimeControlPreset`: da quando la fascia viene dal
   * database, il valore e' una stringa, e l'unione chiusa dei preset non la
   * accetterebbe. A valle non cambia niente — `swiss-handler.ts` gia' fa
   * `tournament.time_control as TimeControlPreset` su un valore letto dal DB, e
   * `resolveTimeControl` interpreta la chiave da sola — e in piu' ora anche lo
   * Swiss puo' girare su una cadenza fuori tabella, quelle che la griglia
   * ammette ma la tabella dei preset non nomina.
   */
  timeControl: TimeControlKey;
  variant: string;
  durationMinutes: number;
  hour: number;   // CET hour (0-23)
  minute: number;  // CET minute (0-59)
  maxRounds: number;
  firstMoveLottery: boolean;
  /** Giorni in cui la fascia esce, `0` = domenica. Vedi `ArenaTemplate.daysOfWeek`. */
  daysOfWeek: readonly number[];
  /** Fascia spenta: resta in tabella, non produce tornei. */
  enabled: boolean;
}

/**
 * Tournament entity
 */
export interface Tournament {
  id: string;
  name: string;
  description: string | null;

  // Game settings
  timeControl: TimeControlPreset;
  variant: GameVariant;

  // Tournament timing
  durationMinutes: number;
  status: TournamentStatus;
  startsAt: Date | string;
  endsAt: Date | string | null;

  // Creator
  createdBy: string | null;
  createdAt: Date | string;

  // Restrictions
  minRating: number;
  maxRating: number;

  // Settings
  isRated: boolean;

  // Stats
  playerCount: number;
  gamesCount: number;

  // Swiss fields
  format: TournamentFormat;
  currentRound: number;
  totalRounds: number;
  /**
   * Con che apertura comincia ogni partita del torneo.
   *
   * Colonna `tournaments.opening_mode`, NOT NULL DEFAULT 'free' dalla migration
   * 101. Qui NON c'era niente: l'entita' condivisa non aveva ne'
   * `firstMoveLottery` ne' `ballotCeiling`, che pure esistevano in tabella
   * dalla 010 e dalla 089 — il tipo condiviso era indietro rispetto al
   * database, e chi leggeva un `Tournament` non aveva modo di sapere con che
   * apertura si sarebbe giocato.
   */
  openingMode: OpeningMode;
}

/**
 * Tournament creation input
 */
export interface CreateTournamentInput {
  name: string;
  description?: string;
  timeControl: TimeControlPreset;
  variant?: GameVariant;
  durationMinutes: number;
  startsAt: Date | string;
  minRating?: number;
  maxRating?: number;
  isRated?: boolean;
}

/**
 * Tournament participant data
 */
export interface TournamentParticipant {
  tournamentId: string;
  userId: string;
  points: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesDrawn: number;
  gamesLost: number;
  currentStreak: number;
  bestStreak: number;
  status: ParticipantStatus;
  isAvailable: boolean;
  joinedAt: Date | string;
  lastGameAt: Date | string | null;
}

/**
 * Tournament standings entry (for leaderboard display)
 */
export interface TournamentStanding {
  rank: number;
  userId: string;
  username: string;
  rating: number;
  points: number;
  gamesPlayed: number;
  gamesWon: number;
  currentStreak: number;
  isPlaying: boolean;
  isOnline: boolean;
  // Swiss tiebreaker fields (optional - only present for Swiss format)
  buchholz?: number;
  sonnebornBerger?: number;
  /** Rating guadagnato (o perso) nelle partite di questo torneo: il quinto spareggio. */
  variazioneRating?: number;
}

/**
 * Tournament game link
 */
export interface TournamentGame {
  gameId: string;
  tournamentId: string;
  roundNumber: number;
}

/**
 * Match found event data
 */
export interface TournamentMatchData {
  gameId: string;
  roomId: string;
  opponentId: string;
  opponentUsername: string;
  opponentRating: number;
  color: 'white' | 'black';
  roundNumber?: number;
  /** Quale manche: 1 andata, 2 ritorno (migration 105, 113). 1 a manche unica. */
  leg?: number;
  /**
   * Quante manche ha questo accoppiamento: 2 solo in andata e ritorno, 1
   * altrimenti. Assente dai server piu' vecchi del campo, dove vale 1.
   *
   * Senza il totale, `leg` da solo non dice niente: vale 1 anche a manche
   * unica, e il client che lo leggeva come «c'e' una manche» annunciava «1/2»
   * su ogni partita di torneo.
   */
  totalLegs?: number;
}

/**
 * Tournament list item (for displaying in list views)
 */
export interface TournamentListItem {
  id: string;
  name: string;
  timeControl: TimeControlPreset;
  variant: GameVariant;
  durationMinutes: number;
  status: TournamentStatus;
  startsAt: Date | string;
  playerCount: number;
  isRated: boolean;
  format: TournamentFormat;
  currentRound: number;
  totalRounds: number;
  /** Con che apertura si gioca. Vedi `Tournament.openingMode`. */
  openingMode: OpeningMode;
}

/**
 * Tournament with full details including standings
 */
export interface TournamentDetail extends Tournament {
  standings: TournamentStanding[];
  isJoined: boolean;
  myParticipant: TournamentParticipant | null;
}


/**
 * Quanto durera' il torneo, piu' o meno.
 *
 * Serve a due posti, ed e' per questo che sta qui e non in uno dei due: il
 * modale la mostra mentre si compila («8 giocatori, girone, blitz 5+3 → circa
 * 1h45, fine verso le 22:45») e il server ne ricava `duration_minutes`, che e'
 * il tempo dopo il quale il ciclo di vita chiude il torneo.
 *
 * Vale piu' di qualsiasi tetto sul numero di giocatori: chi crea sceglie con
 * l'informazione in mano invece di sbattere contro un limite che non capisce.
 *
 * Il conto di un turno: due orologi, circa quaranta mosse a testa per l'
 * incremento, piu' due minuti di cambio turno (l'attesa fra un turno e l'altro
 * piu' il tempo che si perde con chi non si presenta). E' una stima, non una
 * promessa: le partite vere finiscono quasi sempre prima del tempo pieno.
 */
export function estimateTournamentMinutes(input: {
  format: TournamentFormat;
  /**
   * I POSTI del torneo. Facoltativo: uno svizzero puo' aprirsi senza limite di
   * iscritti, e li' non esiste nessun numero da cui dedurre i turni.
   */
  players?: number;
  timeControl: string;
  /**
   * Andata e ritorno: ogni accoppiamento si gioca due volte DENTRO lo stesso
   * turno, colori invertiti (migration 105 per lo svizzero, 113 per il girone).
   *
   * Tocca `minutesPerRound`, non `rounds`, e adesso e' l'UNICA parola: accanto
   * c'era `doubleRound`, che raddoppiava i turni del girone perche' li' il
   * ritorno ripeteva il calendario. Da quando anche il girone rigioca dentro il
   * turno le due dicevano la stessa cosa in due modi incompatibili, e quella
   * sbagliata se n'e' andata. (Era anche gia' codice morto: l'unico chiamante
   * di produzione e' lo scheduler degli svizzeri di calendario, che passa
   * `format: 'swiss'`; il web usa `intervalloItaloSvizzero`.)
   */
  twoLeg?: boolean;
  maxRounds?: number;
}): { rounds: number; minutesPerRound: number; totalMinutes: number } {
  /* Il conto di un turno sta in `minutiDiUnTurno`, qui sotto: lo divide con
     l'italo-svizzero, che dello stesso numero ha bisogno per ogni taglia di
     campo possibile. Due copie della stessa formula sarebbero due durate
     diverse per lo stesso torneo. */
  const minutesPerRound = minutiDiUnTurno(input.timeControl, input.twoLeg ?? false);

  let rounds: number;

  /*
   * Senza posti — lo svizzero a iscrizione libera — i turni sono quelli
   * dichiarati da chi ha aperto il torneo, e nient'altro: non c'e' nessun N da
   * cui ricavarli, e nemmeno il tetto N−1 si puo' applicare (il taglio vero lo
   * fa l'avvio, quando gli iscritti si contano). E' per questo che li' il campo
   * dei turni e' obbligatorio: senza, `duration_minutes` non esisterebbe.
   */
  if (input.players === undefined) {
    rounds = input.maxRounds ?? 0;
    return { rounds, minutesPerRound, totalMinutes: rounds * minutesPerRound };
  }

  if (input.format === 'round_robin') {
    /*
     * Nel girone i turni sono la TABELLA, e `maxRounds` non li tocca: il server
     * all'avvio li ricava da `roundRobinTotalRounds` e ignora `max_rounds`.
     * Applicarlo qui darebbe una durata piu' corta del torneo che verra'
     * giocato — cioe' un torneo chiuso a forza a meta'.
     *
     * La formula non e' piu' ricopiata qui: e' `turniGirone`, la stessa che
     * usa l'accoppiatore. Erano due copie, e una delle due ha continuato a
     * raddoppiare i turni per l'andata e ritorno anche quando l'altra aveva
     * smesso.
     */
    rounds = turniGirone(input.players);
  } else {
    /*
     * Due tetti diversi, e non per distrazione.
     *
     * DICHIARATO: chi organizza ha chiesto quei turni, e il tetto e' quello del
     * campo con la regola della parita' — N−1 coi pari, N coi dispari, perche'
     * li' a ogni turno uno riposa. Il numero e' una DICHIARAZIONE, non un `min`
     * sul conto automatico: trattarlo come un tetto voleva dire nove turni
     * annunciati e una durata da quattro, cioe' il torneo chiuso a meta' dal
     * ciclo di vita.
     *
     * AUTOMATICO: resta `clamp(ceil(log2 N), 3, 7)` tagliato a N−1, com'e'
     * oggi, anche coi dispari. Non e' una svista ma una decisione misurata
     * (`swiss-handler`: alzare l'annuncio automatico a N aprirebbe turni in cui
     * l'accoppiatore e' costretto alla rivincita). Chi dichiara di piu' lo fa
     * apposta, e il modale lo avvisa.
     */
    const tettoDichiarato = input.players % 2 === 0 ? input.players - 1 : input.players;
    const tettoAutomatico = Math.max(input.players - 1, 0);
    const automatici = input.players <= 1
      ? 0
      : Math.max(3, Math.min(7, Math.ceil(Math.log2(input.players))));
    rounds = input.maxRounds !== undefined
      ? Math.max(0, Math.min(input.maxRounds, tettoDichiarato))
      : Math.min(automatici, tettoAutomatico);
  }

  return { rounds, minutesPerRound, totalMinutes: rounds * minutesPerRound };
}

/* ──────────────────────────────────────────────────────────────────────────
 * Il torneo ITALO-SVIZZERO: un formato solo, scelto all'ora d'inizio.
 *
 * Chi apre un torneo non sa quanti verranno, e fin qui doveva deciderlo lo
 * stesso: girone o svizzero, e quanti turni. Due scelte tecniche prese col
 * numero che conta — gli iscritti — ancora ignoto, e l'audit dei tornei ha
 * misurato che cosa producevano (lo svizzero annunciava piu' turni di quanti
 * l'accoppiatore ne potesse produrre).
 *
 * Adesso chi apre sceglie una SOGLIA, e il resto lo decide l'avvio, quando gli
 * iscritti si contano davvero: sotto soglia girone all'italiana, da li' in su
 * sistema svizzero, e i turni da una tabella fissa.
 *
 * Quello che segue e' l'unica fonte di quella decisione: la usano l'avvio del
 * torneo (`swiss-handler`), la durata che il server scrive in tabella
 * (`tournament.controller`) e la riga del riepilogo del modale. Tre lettori,
 * una tabella: se divergessero, il torneo giocato non sarebbe quello promesso.
 *
 * Una cosa la decide QUI e non chi apre il torneo: con TRE o QUATTRO iscritti —
 * il tre e' il pavimento, e quindi il girone piu' piccolo che si possa aprire —
 * il girone si gioca sempre anche di ritorno (`ritornoObbligatorio`), perche' la
 * tabella di Berger su un campo cosi' piccolo fa tre turni, e tre turni da una
 * partita sola non fanno una classifica.
 * ────────────────────────────────────────────────────────────────────────── */

/** La soglia di difetto: da sette iscritti in su si gioca svizzero. */
export const SOGLIA_SVIZZERO_PREDEFINITA = 7;
/**
 * Il minimo: SETTE, ed e' anche il difetto — la soglia piu' bassa che si possa
 * chiedere e' quella che si trova gia' scritta.
 *
 * Fino a SEI iscritti il girone e' sempre la cosa giusta: in un campo cosi'
 * piccolo tutti incontrano tutti comunque, e l'accoppiamento a punteggio non
 * farebbe che togliere una partita a qualcuno.
 *
 * ── Era sei, e il sette non e' prudenza: e' una misura ──────────────────────
 *
 * Col sei si poteva chiedere uno svizzero a SEI iscritti, e quello e' l'unico
 * campo piccolo che la soglia rendesse raggiungibile (a cinque il girone
 * scattava comunque). L'audit del 21 settembre l'ha misurato su 500 tornei
 * simulati per ciascun numero di iscritti: a sei iscritti il 20,6% degli
 * svizzeri finisce con una RIVINCITA, e la forza bruta esaustiva ha provato che
 * non e' colpa dell'accoppiatore — a sei iscritti cinque turni non si possono
 * giocare senza ripetere un incontro (`docs/AUDIT-TORNEI-2026-09-21.md`,
 * «Critico 1»). Il difetto non stava nel come si accoppia ma nel numero di
 * turni promesso, e il modo di non promettere quei turni e' non fare quello
 * svizzero: a sei iscritti si gioca il girone, che di turni ne fa cinque
 * facendo incontrare tutti una volta sola.
 */
export const SOGLIA_SVIZZERO_MINIMA = 7;
/**
 * Il massimo: VENTI, cioe' «girone fino a diciannove iscritti».
 *
 * Era VENTUNO, che voleva dire «girone fino a venti» — il piu' lontano dove la
 * tabella di Berger arrivi. Il venti e' una scelta di PRODOTTO e non un vincolo
 * nuovo: la tendina si legge «Svizzero da 20», e chiedere un girone da venti
 * persone vuol dire diciannove turni, cioe' un torneo che in una sera non
 * finisce. Cio' che si perde e' la sola voce che diceva «sempre girone» quando
 * i posti sono esattamente venti; con diciannove posti quella voce c'e' ancora.
 */
export const SOGLIA_SVIZZERO_MASSIMA = 20;
/**
 * Quanti turni in piu' si possono CHIEDERE: due.
 *
 * Era una domanda booleana — «un turno in piu'?» — ed e' diventata un numero
 * perche' una serata puo' voler durare due turni di piu' del minimo, non solo
 * uno. Il tetto e' basso di proposito: la tabella di `turniSvizzeroPerIscritti`
 * e' tarata su «quanti turni servono a decidere chi ha vinto», e uno o due in
 * piu' sono una serata piu' lunga; dal terzo in su si tornerebbe a DICHIARARE i
 * turni, che la migration 107 ha chiuso su una misura e non va riaperto.
 *
 * ⚠️ Non e' il tetto dei TURNI, e' il tetto della DOMANDA. Quello dei turni
 * resta `turniLeciti(N) − 1` e lo applica `turniSvizzeroPerIscritti`, dove gli
 * iscritti esistono. Il gemello di questa costante e' il CHECK
 * `tournaments_extra_rounds_range` (migration 116), che e' l'unico tetto che il
 * database possa guardare: N, a creazione, non c'e' ancora.
 */
export const TURNI_EXTRA_MASSIMI = 2;
/**
 * I posti in TABELLA del girone: venti, e non scendono col massimo della soglia.
 *
 * ⚠️ Non e' piu' `SOGLIA_SVIZZERO_MASSIMA` meno uno, e il legame fra i due
 * numeri non e' piu' diretto. Col massimo a venti il girone piu' grande ha
 * DICIANNOVE iscritti, che sono dispari: `turniGirone` arrotonda al pari
 * superiore e la tabella di Berger gioca su VENTI posti, uno dei quali riposa a
 * ogni turno. Il vincolo `tournament_participants_seat_range` (migration 094)
 * serve quindi ancora per intero, fino a venti.
 *
 * Il gemello a database e' il vincolo della 094; quello del server e'
 * `MAX_GIOCATORI_PER_FORMATO.round_robin` (tournament.validation.ts). Se questo
 * numero cambia, cambiano tutti e tre.
 */
export const POSTI_MASSIMI_GIRONE = 20;

/**
 * Lo SBARRAMENTO: i punteggi minimi che un torneo valutato puo' chiedere.
 *
 * Una scala e non un numero libero, e per le stesse due ragioni della soglia:
 * la tendina si sceglie con un pollice invece di aprire la tastiera, e lo schema
 * del server ha una lista da confrontare invece di un intervallo che accetta il
 * 1437 senza sapere perche'.
 *
 * Lo ZERO vuol dire NESSUNO sbarramento, ed e' il difetto: e' anche il valore
 * che la colonna `min_rating` ha da sempre (migration 003), quindi i tornei
 * esistenti sono gia' «senza sbarramento» senza che nessuno li tocchi.
 *
 * Il primo gradino vero e' MILLE perche' mille e' il punteggio di casa — quello
 * con cui nasce ogni iscritto, e quello che `ratingPerLaFascia` restituisce a chi
 * non ha mai giocato quella variante a quella cadenza. «Almeno 1000» vuol dire
 * quindi «esordienti esclusi», ed e' la richiesta piu' bassa che dica qualcosa.
 *
 * ⚠️ Vale SOLO sui tornei valutati, e non e' una scelta di prodotto: in un
 * amichevole il punteggio non si muove, quindi uno sbarramento non protegge
 * niente e chiuderebbe la porta per nulla. Lo fa valere il controller, che
 * azzera il numero quando `is_rated` esce spento (anche per un'apertura troppo
 * forte a una manche: vedi `valutatoConQuestaApertura`).
 */
export const SBARRAMENTI: readonly number[] = [
  0, 1000, 1100, 1200, 1300, 1400, 1500, 1600, 1800, 2000,
];

/** Nessuno sbarramento: il difetto, e il valore storico della colonna. */
export const SBARRAMENTO_ASSENTE = 0;

/**
 * Il capo ALTO della fascia quando nessuno ha messo un tetto: 9999.
 *
 * E' il `DEFAULT` storico di `max_rating` (migration 003), e i tornei degli
 * utenti non lo cambiano — lo sbarramento e' un pavimento, non una fascia
 * chiusa. Sta qui perche' lo devono scrivere in due: il repository, quando la
 * colonna non arriva, e il controller, che misura la fascia dell'organizzatore
 * PRIMA che la riga esista. Se i due numeri divergessero, la creazione userebbe
 * una fascia diversa da quella che poi si trova in tabella.
 */
export const FASCIA_SENZA_TETTO = 9999;

/**
 * Il campo sotto cui un girone di sola andata e' troppo corto: QUATTRO iscritti.
 *
 * Sono i due numeri che un girone cosi' piccolo lo danno — TRE e QUATTRO,
 * perche' il pavimento degli iscritti e' tre (`PAVIMENTO_ISCRITTI_TORNEO`,
 * tournament.validation.ts) — e la tabella di Berger li gioca entrambi sugli
 * stessi QUATTRO posti: TRE TURNI, col quarto posto vero a quattro iscritti e
 * un fantasma a tre (li' uno riposa a ogni turno, un turno a testa). Tre turni
 * da una partita sola non bastano a fare una classifica: una sconfitta al primo
 * e il torneo e' gia' deciso.
 *
 * Il confronto e' quindi `<=` e non `===`: non e' una concessione alle righe
 * vecchie, e' la regola per tutti e due i campi che la tabella mette insieme.
 */
export const ISCRITTI_GIRONE_TROPPO_CORTO = 4;

/**
 * Il ritorno si gioca COMUNQUE?
 *
 * Solo nel girone, e solo fino a quattro iscritti: li' l'andata e ritorno non
 * e' piu' una preferenza di chi apre il torneo ma l'unico modo di DOPPIARE le
 * partite di ciascuno — sei a quattro iscritti invece di tre, quattro a tre
 * invece di due, perche' a tre un turno su tre si riposa. Nello svizzero non
 * scatta mai: un campo cosi' piccolo uno svizzero non lo produce nemmeno (la
 * soglia minima e' sette).
 *
 * ⚠️ Qui c'era scritto «l'unico modo di avere sei TURNI invece di tre», e la
 * differenza non e' una sfumatura di parole: da quando il ritorno si gioca
 * dentro il turno (come nello svizzero, e non piu' ripetendo il calendario) un
 * girone da tre o da quattro resta di TRE turni anche con l'andata e ritorno
 * acceso.
 * Quello che raddoppia sono le partite, ed e' sempre stato quello il punto —
 * «una sconfitta al primo e il torneo e' gia' deciso» parla di partite da
 * recuperare, non di righe di calendario. La regola quindi vale ancora per
 * intero; a non valere piu' e' solo il modo in cui la si raccontava.
 *
 * Vive accanto a `esitoDelTorneo`, che la applica e la RESTITUISCE: chi scrive
 * la riga all'avvio deve scrivere la bandiera applicata, non quella chiesta a
 * creazione, perche' e' quella che decide se il turno apre una partita o due.
 */
export function ritornoObbligatorio(
  iscritti: number,
  format: 'swiss' | 'round_robin',
): boolean {
  return format === 'round_robin' && iscritti <= ISCRITTI_GIRONE_TROPPO_CORTO;
}

/**
 * Quanti turni un campo di N iscritti puo' REGGERE.
 *
 * Con N giocatori gli avversari diversi sono N−1, e coi dispari a ogni turno
 * uno riposa — quindi un turno in piu' ci sta. Oltre questo numero non si
 * aggiungono partite, si aggiungono rivincite.
 *
 * Il server aveva la sua copia (`turniLecitiPerPosti`), che applicava lo stesso
 * conto ai POSTI per rifiutare un numero di turni dichiarato: se n'e' andata coi
 * turni dichiarati, e il tetto vive adesso solo qui, applicato agli iscritti veri.
 */
export function turniLeciti(iscritti: number): number {
  if (iscritti < 2) return 0;
  return iscritti % 2 === 0 ? iscritti - 1 : iscritti;
}

/**
 * Un formato a turni, cioe' tutto tranne l'arena.
 *
 * Sta QUI e non piu' solo nel server perche' adesso serve anche al web: la
 * sala, la partita e la pagina del dettaglio chiedevano `format === 'swiss'`,
 * e con l'unificazione il girone e' diventato l'esito normale dei tornei
 * piccoli — un ramo `else` che finiva sull'arena avrebbe mostrato i punteggi
 * dell'arena a un girone.
 */
export function isRoundBasedFormat(format: string | null | undefined): boolean {
  return format === 'swiss' || format === 'round_robin';
}

/**
 * Quanto vale il riposo, e la risposta dipende dal FORMATO.
 *
 * Nello **svizzero** vale un turno intero: `SWISS_SCORING.BYE` dice «come una
 * vittoria» (C.04.1 art. c: *«as many points as are rewarded for a win, unless
 * the rules of the tournament state otherwise»*), e moltiplicarlo per le manche
 * e' l'unico modo di dire «un turno vinto senza giocare» — in un turno a due
 * manche chi gioca fa due partite, e un bye da una sola sarebbe mezzo turno,
 * cioe' una penalizzazione per chi ha avuto la sfortuna del numero dispari e
 * non ha scelto niente.
 *
 * Nel **girone** vale ZERO, e non e' un'eccezione: e' che l'art. c e' una
 * regola dello svizzero, e il girone ha la sua fonte. L'annesso di Berger dice
 * soltanto *«where there is an odd number of players, the highest number counts
 * as a bye»*, e l'FMJD 5.11.1.2 non gli attribuisce nessun punto. Con N dispari
 * si gioca sulla tabella di N+1, e chi incontra il posto fantasma non gioca:
 * non e' un turno vinto, e' un turno che non esiste.
 *
 * A girone completo la differenza non sposta l'ordine — ciascuno riposa
 * esattamente una volta, quindi due punti a testa sono una costante — ma
 * gonfiava i totali, metteva in testa chi aveva gia' riposato per tutta la
 * durata del torneo, e in una chiusura anticipata poteva incoronare.
 * (Era la decisione aperta M1 di `docs/AUDIT-TORNEI-DATI-2026-09-24.md`,
 * presa il 15 ottobre 2026.)
 *
 * Il `format` arriva dal database, quindi e' una stringa e non l'unione chiusa:
 * stessa firma di `isRoundBasedFormat` qui sopra e di `restaInClassifica` nel
 * server. Una riga senza formato si comporta da svizzero, che e' l'unico
 * formato creabile e quindi il significato di quel NULL.
 */
export function puntiPerIlRiposo(format: string | null | undefined, manche: number): number {
  return format === 'round_robin' ? 0 : SWISS_SCORING.BYE * manche;
}

/**
 * I turni di un GIRONE: la tabella di Berger, cioe' N−1 arrotondato al pari
 * superiore. **E basta** — l'andata e ritorno non entra nel conto.
 *
 * ⚠️ Prendeva un secondo argomento, `andataERitorno`, e raddoppiava. Non lo
 * prende piu', e la ragione e' che il girone ha cambiato il modo di giocare il
 * ritorno: non ripete piu' l'intero calendario a colori invertiti (un girone
 * da quattro faceva SEI turni da una partita) ma rigioca l'accoppiamento
 * DENTRO lo stesso turno, come lo svizzero dalla migration 105 — tre turni da
 * due partite. Le partite giocate sono le stesse; i turni sono la meta'.
 *
 * Il posto dove «turni» e «partite» tornano a divergere e' uno solo, ed e'
 * `partitePerGiocatore` qui sotto.
 *
 * E' la stessa formula di `roundRobinTotalRounds` (round-robin-pairing.service),
 * che infatti e' un rimando a questa: il server ne ha bisogno per accoppiare,
 * il modale per dire quanto dura.
 */
export function turniGirone(iscritti: number): number {
  if (iscritti < 2) return 0;
  const postiInTabella = iscritti % 2 === 0 ? iscritti : iscritti + 1;
  return postiInTabella - 1;
}

/**
 * Quante PARTITE gioca ciascuno: i turni, per due se si gioca anche il ritorno.
 *
 * Esiste perche' da quando le due manche stanno dentro il turno «turni» e
 * «partite di ciascuno» non sono piu' lo stesso numero, e c'e' un posto in cui
 * la differenza cambia un verdetto: l'art. 5.9 FMJD (`restaInClassifica`,
 * server/tournament.types.ts) toglie dalla classifica il ritirato che non ha
 * giocato almeno META' delle partite previste. Misurarlo in turni, in un
 * girone a due manche, dimezzerebbe la soglia senza che nessuno se ne accorga.
 *
 * ⚠️ Vale per i due formati a turni, ma serve DAVVERO solo nel girone: nello
 * svizzero l'art. 5.9 non toglie nessuno che abbia giocato (li' ognuno fa il
 * proprio percorso), quindi il numero si calcola e non si usa.
 *
 * Sui tornei STORICI torna il numero giusto senza nessun caso speciale: un
 * girone del vecchio mondo ha `two_leg = FALSE` e `total_rounds` gia'
 * raddoppiato dal calendario ripetuto, quindi `rounds * 1` sono esattamente le
 * partite che quei giocatori hanno giocato. E' il motivo per cui la migration
 * 113 non converte le righe finite: lasciate com'erano, si leggono ancora bene.
 */
export function partitePerGiocatore(rounds: number, andataERitorno = false): number {
  return rounds * (andataERitorno ? 2 : 1);
}

/**
 * I turni di uno SVIZZERO, per numero esatto di iscritti.
 *
 * La tabella e' fissa e non si discute: raddoppiando il campo si aggiunge un
 * turno, perche' ogni turno dimezza il numero di chi puo' ancora essere primo
 * a punteggio pieno. Non e' `ceil(log2 N)` — che e' il conto di un torneo a
 * eliminazione, e in uno svizzero lascerebbe due o tre persone appaiate in
 * testa senza che si siano mai incontrate — ma quel conto PIU' UNO, con
 * l'ultimo scalino allargato dove i campi veri si fermano.
 *
 * Il taglio a `turniLeciti` morde solo in coda: con sette iscritti — il minimo
 * che la soglia consenta — i turni leciti sono sette e la tabella ne chiede
 * quattro, quindi non morde affatto. Resta per chi legga questa funzione da
 * sola, senza la soglia davanti.
 *
 * ── I TURNI EXTRA, e perche' hanno un tetto loro ─────────────────────────────
 *
 * Chi apre il torneo puo' chiedere uno o due turni in piu' di quelli della
 * tabella (`TURNI_EXTRA_MASSIMI`): la tabella e' tarata su «quanti turni
 * servono a decidere chi ha vinto», e una serata puo' voler durare uno o due
 * turni di piu' di quel minimo.
 *
 * Non e' pero' un `+n` libero, e il tetto NON e' `turniLeciti`. L'audit del 21
 * settembre (`docs/AUDIT-TORNEI-2026-09-21.md`, critico 1) ha MISURATO il
 * confine, facendo girare l'accoppiatore vero su cinquecento tornei per ogni
 * N: con cinque iscritti a quattro turni il 23.6% dei tornei finisce con una
 * rivincita, con sei a cinque turni il 20.6% — e la forza bruta sul grafo dei
 * «non ancora incontrati» dice che in quei casi un turno pulito NON ESISTE.
 * Non e' un difetto dell'accoppiatore: e' un turno promesso che il campo non
 * puo' contenere. `turniLeciti` conta gli avversari diversi (necessario) ma
 * non sa se un accoppiamento senza rivincite li raggiunga (non sufficiente):
 * a sei iscritti, dopo tre turni il grafo e' 2-regolare, e se viene fuori come
 * due triangoli invece di un ciclo il quarto turno e' impossibile.
 *
 * Il tetto e' quindi `turniLeciti − 1`, che e' il piu' alto che la misura
 * assolva. E' un tetto sul TOTALE dei turni, non sull'incremento: per questo
 * regge il secondo turno extra senza ritocchi. In pratica non morde affatto —
 * mordeva a sei iscritti, dove il turno extra non si vedeva, e da quando
 * `SOGLIA_SVIZZERO_MINIMA` e' sette uno svizzero a sei iscritti non si puo'
 * nemmeno chiedere. Resta perche' questa funzione si legge anche da sola, e
 * perche' e' la misura a garantire il turno, non la soglia.
 * `Math.max(daTabella, …)` perche' una richiesta di allungare non deve mai
 * poter ACCORCIARE.
 *
 * Che i turni cosi' concessi si ACCOPPINO davvero non e' una deduzione:
 * `swiss-pairing.simulazione.test.ts` fa girare l'accoppiatore vero, e i tre
 * campi dove il `+2` cade — sette iscritti a sei turni, otto a sei, nove a
 * sette — ci sono tutti e tre, puliti.
 *
 * ⚠️ `Math.trunc` e i due estremi: il numero arriva da una riga di database, e
 * una riga scritta a mano non deve poter allungare un torneo oltre quello che
 * il comando consente. Il tetto di tabella (migration 116) e' la rete, questa
 * e' la cintura.
 */
export function turniSvizzeroPerIscritti(iscritti: number, turniExtra = 0): number {
  if (iscritti < 2) return 0;
  const daTabella = iscritti <= 8 ? 4
    : iscritti <= 16 ? 5
    : iscritti <= 32 ? 6
    : iscritti <= 64 ? 7
    : iscritti <= 128 ? 8
    : iscritti <= 192 ? 9
    : 10;
  const extra = Math.max(0, Math.min(TURNI_EXTRA_MASSIMI, Math.trunc(turniExtra)));
  const chiesti = extra > 0
    ? Math.min(daTabella + extra, Math.max(daTabella, turniLeciti(iscritti) - 1))
    : daTabella;
  return Math.min(chiesti, turniLeciti(iscritti));
}

/**
 * Che torneo verra' fuori, con questi iscritti e questa soglia.
 *
 * L'UNICA fonte: la chiamano l'avvio (che ci scrive `format` e `total_rounds`)
 * e le due stime. «Sotto soglia» e' stretto — con soglia 7 e sette iscritti si
 * gioca svizzero — perche' e' cosi' che la soglia si legge a schermo: «da
 * sette in poi».
 *
 * Restituisce anche l'ANDATA E RITORNO, e non e' quella ricevuta: con tre o
 * quattro iscritti il girone si gioca di ritorno comunque (`ritornoObbligatorio`),
 * perche' tre partite non fanno una classifica. Chi scrive la riga all'avvio
 * deve scrivere QUESTA, non quella chiesta a creazione: e' la bandiera che
 * decide se ogni turno apre una partita o due, e una riga che dice «no» su un
 * torneo che il ritorno lo gioca comunque spegnerebbe il valutato per niente
 * (`valutatoConQuestaApertura`).
 *
 * ⚠️ I `rounds` del girone NON raddoppiano piu' con l'andata e ritorno: le due
 * manche stanno dentro il turno, quindi il calendario resta quello di Berger.
 *
 * I TURNI EXTRA entrano solo nel ramo svizzero, e non per una svista: il girone
 * ha un calendario COMPLETO — tutti incontrano tutti — e un turno in piu' la'
 * non e' una partita in piu', e' una rivincita per tutti. Chi volesse rigiocare
 * il girone chiede l'andata e ritorno, che e' la domanda giusta per quel
 * formato. Valgono quindi solo dove i turni sono un CAMPIONE del campo.
 */
export function esitoDelTorneo(input: {
  iscritti: number;
  soglia: number;
  andataERitorno?: boolean;
  turniExtra?: number;
}): { format: 'swiss' | 'round_robin'; rounds: number; andataERitorno: boolean } {
  /*
   * La rete, non la regola: `SOGLIA_SVIZZERO_MASSIMA` gia' garantisce che
   * sotto soglia non si possa passare i venti iscritti — e da quando il massimo
   * e' venti, nemmeno i diciannove. Se pero' una riga vecchia, o una soglia
   * scritta a mano, ci arrivasse lo stesso, il girone
   * chiederebbe alla tabella di Berger un posto che il database rifiuta e il
   * torneo morirebbe all'avvio. Meglio uno svizzero di un torneo rotto.
   */
  const girone = input.iscritti < input.soglia && input.iscritti <= POSTI_MASSIMI_GIRONE;
  const format = girone ? 'round_robin' : 'swiss';
  /* Un OR, non un rimpiazzo: la regola puo' solo ACCENDERE il ritorno. Un
     torneo aperto con l'andata e ritorno gia' chiesto resta quello che era. */
  const andataERitorno = (input.andataERitorno ?? false)
    || ritornoObbligatorio(input.iscritti, format);
  return format === 'round_robin'
    ? { format, rounds: turniGirone(input.iscritti), andataERitorno }
    : {
      format,
      rounds: turniSvizzeroPerIscritti(input.iscritti, input.turniExtra ?? 0),
      andataERitorno,
    };
}

/**
 * Quanto dura UN turno: due orologi, circa quaranta mosse a testa per
 * l'incremento, piu' due minuti di cambio turno (l'attesa fra un turno e
 * l'altro piu' il tempo che si perde con chi non si presenta).
 *
 * Il «+2» resta UNO anche con due manche: fra l'andata e il ritorno non si
 * riaccoppia nessuno — sono gli stessi due, subito — quindi c'e' solo il conto
 * alla rovescia fra le due partite, non un turno da aprire. E nemmeno l'assente
 * costa attesa: o il ritorno viene aggiudicato all'istante, o la stanza si apre
 * con il suo orologio che corre, che e' tempo gia' dentro `minutiPartita`.
 *
 * ⚠️ Da quando anche il GIRONE gioca il ritorno dentro il turno (migration 113)
 * questa riga accorcia la durata annunciata di un girone di andata e ritorno:
 * venti posti passano da `38·(p+2)` a `19·(2p+2)`, trentotto minuti in meno.
 * Non e' margine perso ed e' bene non «ripararlo»: il `+2` paga un CAMBIO
 * TURNO, e i cambi turno adesso sono davvero la meta'.
 */
export function minutiDiUnTurno(timeControl: string, dueManche = false): number {
  const clock = resolveTimeControl(timeControl);
  const minutiOrologio = clock ? clock.initialTime / 60_000 : 5;
  const incrementoSecondi = clock ? clock.increment / 1000 : 0;
  const minutiPartita = Math.ceil(2 * (minutiOrologio + (40 * incrementoSecondi) / 60));
  return minutiPartita * (dueManche ? 2 : 1) + 2;
}

/**
 * L'intervallo di un italo-svizzero: da quanti a quanti turni, e quanto dura
 * nel caso peggiore.
 *
 * I turni si contano SCORRENDO gli iscritti possibili, uno per uno, e non con
 * una formula sui due estremi. Non e' pigrizia: la funzione non e' monotona.
 * Con soglia 7, sei iscritti fanno un girone da CINQUE turni e sette uno
 * svizzero da QUATTRO — il massimo sta in mezzo all'intervallo, non a un capo.
 * Lo stesso vale per i minuti, perche' l'andata e ritorno raddoppia i turni nel
 * girone e la durata del turno nello svizzero. Sono al massimo duecento giri,
 * che e' il tetto dei posti: un ciclo, non una formula, e nemmeno un costo.
 *
 * `minutiTotali` e' il CASO PEGGIORE, ed e' il numero che conta davvero: il
 * server ne ricava `duration_minutes`, cioe' l'istante in cui il ciclo di vita
 * chiude il torneo. Una durata presa sul caso medio spegnerebbe i tornei
 * sfortunati a turni ancora da giocare.
 */
export function intervalloItaloSvizzero(input: {
  minPlayers: number;
  maxPlayers: number;
  soglia: number;
  andataERitorno?: boolean;
  turniExtra?: number;
  timeControl: string;
}): { turniMin: number; turniMax: number; minutiTotali: number } {
  const da = Math.max(2, Math.min(input.minPlayers, input.maxPlayers));
  const a = Math.max(da, input.maxPlayers);

  let turniMin = Number.POSITIVE_INFINITY;
  let turniMax = 0;
  let minutiTotali = 0;

  for (let iscritti = da; iscritti <= a; iscritti++) {
    const esito = esitoDelTorneo({
      iscritti,
      soglia: input.soglia,
      andataERitorno: input.andataERitorno,
      turniExtra: input.turniExtra,
    });
    /* Le due manche allungano il TURNO, in tutti e due i formati: adesso
       ripetono la partita dentro il turno anche nel girone, che prima ripeteva
       il calendario e si raddoppiava i turni. Da qui la guardia
       `esito.format === 'swiss' &&` che stava su questa riga: serviva a non
       contare il ritorno due volte nel girone, e senza il raddoppio dei turni
       non ha piu' niente da proteggere.

       La bandiera e' quella APPLICATA, non quella chiesta: fino a quattro
       iscritti l'esito la accende da solo, e leggere l'esito e' l'unico modo
       di non riderivare una decisione gia' presa. */
    const perTurno = minutiDiUnTurno(input.timeControl, esito.andataERitorno);
    turniMin = Math.min(turniMin, esito.rounds);
    turniMax = Math.max(turniMax, esito.rounds);
    minutiTotali = Math.max(minutiTotali, esito.rounds * perTurno);
  }

  return {
    turniMin: Number.isFinite(turniMin) ? turniMin : 0,
    turniMax,
    minutiTotali,
  };
}
